import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ElementType,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '../primitives';
import * as Icons from '../icons';
import { HairlineCard } from './HairlineCard';
import { StackedRows } from './ResponsiveTable';
import { useIsPhone } from '../hooks/useIsPhone';
import { SortableHeaderCell, type SortDir } from './SortableHeaderCell';
import { RowActionsCompactContext } from './RowActions';
import { dataTableSx, monoCellSx, titleCellSx } from '../table-styles';
import { iconSize } from '@munifocus/tokens';
import { alpha, type Theme } from '@mui/material';

/** One column. `cell` renders; `sortValue` orders; omit it to disable sorting. */
export interface DataTableColumn<Row> {
  key: string;
  label: string;
  cell: (row: Row) => ReactNode;
  /**
   * The value this column sorts by. Omitted, the column's header is not
   * sortable — right for an actions column, wrong for anything carrying data.
   */
  sortValue?: (row: Row) => string | number | null | undefined;
  align?: 'left' | 'center' | 'right';
  /** The column that identifies the row: heavier weight, primary ink. */
  primary?: boolean;
  /** Opaque identifiers (references, slugs): monospace and muted. */
  mono?: boolean;
  /** Never wrap. Use for dates, counts and short codes. */
  nowrap?: boolean;
  /**
   * Hidden until the reader turns it on in the column picker.
   *
   * For a table with more columns than a screen: the default view stays
   * readable and the rest are one click away, rather than every column being
   * present and none of them legible.
   */
  optional?: boolean;
  /** Never offered in the picker — an actions column has nothing to toggle. */
  alwaysVisible?: boolean;
  /**
   * The row's buttons. With `stackOnPhone` they sit at the foot of each card.
   * Defaults to true for the `actions` column `useRowActionsColumn` builds.
   */
  actions?: boolean;
  /**
   * Pinned to the right edge while the rest of the row scrolls under it. The
   * actions column sets this (through `useRowActionsColumn`): when a table is
   * wider than its card, the way into a record must not be the part that has
   * scrolled out of sight.
   */
  sticky?: boolean;
}

export interface DataTableProps<Row> {
  rows: Row[];
  columns: DataTableColumn<Row>[];
  getRowKey: (row: Row) => string;
  /**
   * Enables the search box.
   *
   * Give `of` to filter client-side — the table already holds every row. Give
   * `value`/`onChange` instead when the server does the filtering (a registry
   * too large to ship whole), and the table renders the box but does not touch
   * the rows.
   */
  search?:
    | { placeholder: string; of: (row: Row) => string }
    | { placeholder: string; value: string; onChange: (value: string) => void };
  /** Rows per page, paged in the browser. Omit to render every row. */
  pageSize?: number;
  /**
   * Server-driven paging, for a registry too large to ship whole.
   *
   * Mutually exclusive with `pageSize`: there the table holds every row and
   * slices it; here it holds one page and the server holds the rest, so the
   * count and the pager both have to come from outside.
   */
  pagination?: { page: number; pageCount: number; total?: number; onPage: (page: number) => void };
  /** Extra controls for the toolbar — a scope or status filter, say. */
  filters?: ReactNode;
  /**
   * Row selection, for screens that act on many rows at once.
   *
   * The header checkbox covers the rows currently on screen, not the whole
   * result set: on a server-paged registry the table cannot select what it has
   * not been sent, and a checkbox that silently means "and 4,000 others" is how
   * people disable accounts they never saw.
   */
  selection?: {
    selected: Set<string>;
    onChange: (selected: Set<string>) => void;
    /** Rendered in the toolbar while at least one row is selected. */
    actions?: ReactNode;
    disabled?: boolean;
  };
  initialSortKey?: string;
  initialSortDir?: SortDir;
  isLoading?: boolean;
  /** Shown when there are no rows at all — distinct from "no search matches". */
  emptyLabel?: string;
  /** The screen's primary action, placed in the toolbar. */
  action?: ReactNode;
  /**
   * A mouse convenience: clicking anywhere on the row. Never the only way in —
   * a `<tr>` with a click handler is invisible to the keyboard. Prefer
   * `rowHref` for a row that opens a record; a row that only selects (a side
   * panel, say) is made focusable and answers Enter and Space instead.
   */
  onRowClick?: (row: Row) => void;
  /**
   * Where the row leads. The primary column's cell renders as a real link to
   * it — focusable, announced as a link, and middle-click / "open in new tab"
   * work — and a click anywhere else on the row follows that same link. Pair
   * it with `linkComponent` (the app's router `Link`) so following it is a
   * client-side navigation rather than a page load.
   */
  rowHref?: (row: Row) => string;
  /**
   * Renders the `rowHref` link. Given, it receives the href as `to` (the
   * react-router `Link` contract); omitted, a plain `<a href>` is rendered.
   * `libs/ui` does not depend on a router, so the app supplies it.
   */
  linkComponent?: ElementType;
  /** Below this the columns cannot fit and the wrapper scrolls instead. */
  minWidth?: number;
  /** Names the scroll region for screen readers. Required when `minWidth` is set. */
  label?: string;
  rowSx?: (row: Row) => object | undefined;
  /**
   * Below `sm`, render each row as a stacked card — heading, label/value
   * pairs, buttons at the foot — instead of a table that hides its name and
   * Edit columns behind a sideways scroll. Search, paging and the toolbar are
   * unchanged; sorting follows the current sort.
   */
  stackOnPhone?: boolean;
  /** Accessible name for a stacked card's heading button, when `onRowClick` is set. */
  rowLabel?: (row: Row) => string;
}

/**
 * The table's scroll wrapper. `position: relative` makes it the containing
 * block for the absolutely positioned, visually hidden sort announcements in
 * the header cells: without it they escape the `overflow-x` clip and widen the
 * whole page at phone width (a 390px Tickets screen scrolled sideways by 270px).
 */
const TABLE_SCROLL_SX = { overflowX: 'auto', position: 'relative' } as const;

/** Marks the pinned column's cells, head and body alike. */
const STICKY_CELL_CLASS = 'DataTable-stickyCell';

/**
 * The pinned column has to be opaque — the cells scrolling under it would
 * otherwise show through — yet still carry the row's zebra stripe and hover
 * tint, which are translucent washes on the row. So the cell paints the card
 * colour and lays the same wash over it as an image.
 */
const wash = (color: string) => `linear-gradient(${color}, ${color})`;
const stickySx = {
  [`& .${STICKY_CELL_CLASS}`]: {
    position: 'sticky',
    right: 0,
    zIndex: 1,
    backgroundColor: 'background.paper',
  },
  [`& .MuiTableHead-root .${STICKY_CELL_CLASS}`]: {
    backgroundColor: 'background.default',
    zIndex: 2,
  },
  [`& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .${STICKY_CELL_CLASS}`]: {
    backgroundImage: (theme: Theme) => wash(alpha(theme.palette.text.primary, 0.025)),
  },
  [`& .MuiTableBody-root .MuiTableRow-root:hover .${STICKY_CELL_CLASS}`]: {
    backgroundImage: (theme: Theme) => wash(alpha(theme.palette.primary.main, 0.08)),
  },
  // Only once something is actually scrolling beneath it does the pinned
  // column need an edge — a hairline, like every other border here.
  [`[data-overflowing] & .${STICKY_CELL_CLASS}`]: {
    boxShadow: (theme: Theme) => `inset 1px 0 0 ${theme.palette.divider}`,
  },
} as const;

/**
 * Whether the table needs its row actions compact, and whether it still
 * overflows once they are.
 *
 * Measured, not guessed from the viewport: the same eight-column contracts
 * table fits a 1440px monitor and does not fit a 1240px laptop with the rail
 * open, and a three-column table fits both. The labelled width is remembered
 * while compact — measuring the compact table would say "fits" and flip the
 * labels straight back on, forever.
 */
function useActionsFit(scrollRef: RefObject<HTMLDivElement | null>, deps: unknown[]) {
  const [compact, setCompact] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const labelledWidth = useRef(0);
  const compactRef = useRef(false);

  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const table = scroller?.firstElementChild as HTMLElement | null | undefined;
    if (!scroller || !table || typeof ResizeObserver === 'undefined') return;
    const check = () => {
      const available = scroller.clientWidth;
      // jsdom, or a card not laid out yet: nothing to decide from.
      if (available === 0) return;
      if (!compactRef.current) labelledWidth.current = table.scrollWidth;
      const next = labelledWidth.current > available + 1;
      compactRef.current = next;
      setCompact(next);
      setOverflowing(table.scrollWidth > available + 1);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(scroller);
    observer.observe(table);
    return () => observer.disconnect();
  }, deps);

  return { compact, overflowing };
}

/**
 * The one data table.
 *
 * Every list in the Government and Admin portals renders through this, so that
 * search, sorting, empty states, paging and the visual rhythm are the same
 * wherever an officer meets a table. The two portals are used side by side in
 * the same job; a table that sorts in one and not the other reads as a bug, not
 * as two screens with different needs.
 *
 * It replaced a per-screen copy of the same block. The copies had drifted:
 * nineteen admin screens shared a look that the Government Portal's six
 * administration panels never picked up, half the tables had no empty state,
 * and only the admin registries could be searched at all.
 *
 * Sorting is `localeCompare` with `numeric`, so "F-2026-9" precedes
 * "F-2026-10" and accented municipal names order the way a francophone officer
 * expects. Blanks sort last in both directions: a missing reference is absent
 * information, not the smallest value.
 */
export function DataTable<Row>({
  rows,
  columns,
  getRowKey,
  search,
  pageSize,
  pagination,
  filters,
  selection,
  initialSortKey,
  initialSortDir = 'asc',
  isLoading = false,
  emptyLabel,
  action,
  onRowClick,
  rowHref,
  linkComponent,
  minWidth,
  label,
  rowSx,
  stackOnPhone = false,
  rowLabel,
}: DataTableProps<Row>) {
  const { t } = useTranslation();
  const phone = useIsPhone();
  const stacked = stackOnPhone && phone;
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState(initialSortKey ?? columns[0]?.key ?? '');
  const [sortDir, setSortDir] = useState<SortDir>(initialSortDir);
  const [page, setPage] = useState(0);
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(
    () => new Set(columns.filter((c) => c.optional).map((c) => c.key)),
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const pickable = columns.filter((c) => !c.alwaysVisible);
  const shown = columns.filter((c) => !hidden.has(c.key));
  // The cell that carries the row's link: the column that names the record,
  // or the first one on screen when no column claims that.
  const linkColumnKey = (shown.find((c) => c.primary) ?? shown[0])?.key;
  const clickable = Boolean(onRowClick || rowHref);

  const onRowMouseClick = (row: Row, event: MouseEvent<HTMLTableRowElement>) => {
    // A click that already landed on something interactive inside the row —
    // the link itself, a checkbox, a row action — is that control's to handle.
    const target = event.target as HTMLElement;
    if (target.closest('a, button, input, select, textarea, [role="button"], [role="menuitem"]')) {
      return;
    }
    if (onRowClick) {
      onRowClick(row);
      return;
    }
    // No handler of its own: the row follows its link, so the mouse and the
    // keyboard land in the same place by the same route.
    event.currentTarget.querySelector<HTMLAnchorElement>('a[data-row-link]')?.click();
  };

  // A row that selects rather than navigates has no link to carry the focus,
  // so the row itself takes it and answers the keys a button would.
  const onRowKeyDown = (row: Row, event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick?.(row);
    }
  };

  const toggleSort = (key: string) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const controlled = search && 'onChange' in search;
  const searchValue = controlled ? search.value : query;

  const sorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    // A controlled search is the server's job; filtering again here would hide
    // rows it deliberately returned.
    const filtered =
      q && search && 'of' in search
        ? rows.filter((row) => search.of(row).toLowerCase().includes(q))
        : rows;
    const column = columns.find((c) => c.key === sortKey);
    if (!column?.sortValue) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = column.sortValue?.(a);
      const bv = column.sortValue?.(b);
      if (av == null || av === '') return bv == null || bv === '' ? 0 : 1;
      if (bv == null || bv === '') return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
    });
  }, [rows, columns, query, search, sortKey, sortDir]);

  const pageCount = pagination
    ? pagination.pageCount
    : pageSize
      ? Math.max(1, Math.ceil(sorted.length / pageSize))
      : 1;
  // A search or a background refresh can leave the current page past the end.
  const safePage = pagination ? pagination.page - 1 : Math.min(page, pageCount - 1);
  const visible =
    pageSize && !pagination
      ? sorted.slice(safePage * pageSize, safePage * pageSize + pageSize)
      : sorted;
  const goToPage = (next: number) => {
    if (pagination) pagination.onPage(next + 1);
    else setPage(next);
  };

  const { compact, overflowing } = useActionsFit(scrollRef, [isLoading, minWidth, shown.length]);

  const pageSelectedCount = selection
    ? visible.filter((row) => selection.selected.has(getRowKey(row))).length
    : 0;

  const toggleAllOnPage = (checked: boolean) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    for (const row of visible) {
      if (checked) next.add(getRowKey(row));
      else next.delete(getRowKey(row));
    }
    selection.onChange(next);
  };

  const toggleOne = (key: string, checked: boolean) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (checked) next.add(key);
    else next.delete(key);
    selection.onChange(next);
  };

  const body = (
    <Table
      size="small"
      sx={{ ...(minWidth ? { minWidth } : null), ...dataTableSx, ...stickySx } as object}
    >
      <TableHead>
        <TableRow>
          {selection && (
            <TableCell padding="checkbox">
              <Checkbox
                checked={pageSelectedCount > 0 && pageSelectedCount === visible.length}
                indeterminate={pageSelectedCount > 0 && pageSelectedCount < visible.length}
                disabled={visible.length === 0 || selection.disabled}
                onChange={(e) => toggleAllOnPage(e.target.checked)}
                slotProps={{ input: { 'aria-label': t('common.table.selectAll') } }}
              />
            </TableCell>
          )}
          {shown.map((column) =>
            column.sortValue ? (
              <SortableHeaderCell
                key={column.key}
                label={column.label}
                sortKey={column.key}
                activeKey={sortKey}
                dir={sortDir}
                onSort={toggleSort}
                align={column.align}
                className={column.sticky ? STICKY_CELL_CLASS : undefined}
              />
            ) : (
              <TableCell
                key={column.key}
                align={column.align}
                className={column.sticky ? STICKY_CELL_CLASS : undefined}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {column.label}
              </TableCell>
            ),
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {visible.map((row) => (
          <TableRow
            key={getRowKey(row)}
            hover
            onClick={clickable ? (event) => onRowMouseClick(row, event) : undefined}
            {...(onRowClick && !rowHref
              ? {
                  tabIndex: 0,
                  onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) =>
                    onRowKeyDown(row, event),
                }
              : null)}
            sx={{ ...(clickable ? { cursor: 'pointer' } : null), ...rowSx?.(row) }}
          >
            {/* The head renders a select-all cell whenever `selection` is set,
                so every body row owes one back. Without it each row was a cell
                short and every column after the first slid left under the wrong
                header — the whole table read as scrambled on any page that
                offers selection. */}
            {selection && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selection.selected.has(getRowKey(row))}
                  disabled={selection.disabled}
                  // The row may itself be clickable; selecting is a different
                  // intent and must not also open the record.
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => toggleOne(getRowKey(row), event.target.checked)}
                  slotProps={{ input: { 'aria-label': t('common.table.selectRow') } }}
                />
              </TableCell>
            )}
            {shown.map((column) => (
              <TableCell
                key={column.key}
                align={column.align}
                className={column.sticky ? STICKY_CELL_CLASS : undefined}
                sx={{
                  ...(column.primary ? titleCellSx : { color: 'text.secondary' }),
                  ...(column.mono ? monoCellSx : null),
                  ...(column.nowrap ? { whiteSpace: 'nowrap' } : null),
                }}
              >
                {rowHref && column.key === linkColumnKey ? (
                  <Link
                    component={linkComponent ?? 'a'}
                    {...(linkComponent ? { to: rowHref(row) } : { href: rowHref(row) })}
                    data-row-link=""
                    underline="hover"
                    color="inherit"
                    sx={{ display: 'inline-block' }}
                  >
                    {column.cell(row)}
                  </Link>
                ) : (
                  column.cell(row)
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
        {visible.length === 0 && (
          <TableRow>
            <TableCell
              colSpan={shown.length + (selection ? 1 : 0)}
              sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}
            >
              {/* Two different nothings: nothing here at all, versus nothing
                  matching what was typed. Telling them apart is the difference
                  between "add the first one" and "clear your search". */}
              {rows.length === 0
                ? (emptyLabel ?? t('common.table.empty'))
                : t('common.table.noMatches')}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  return (
    <Stack spacing={2}>
      {(search || action || filters || (selection && selection.selected.size > 0)) && (
        <HairlineCard sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            useFlexGap
            // Wraps. Three filters, a search box, a row count, a column picker
            // and two buttons do not fit a laptop at this app's sidebar width,
            // and without wrapping the row simply ran off the right edge —
            // taking the "add" button with it, on the screens that have one.
            // A control you cannot reach is worse than a control on a second
            // line.
            sx={{ alignItems: { md: 'center' }, flexWrap: { md: 'wrap' } }}
          >
            {filters}
            {search && (
              <TextField
                size="small"
                placeholder={search.placeholder}
                value={searchValue}
                onChange={(e) => {
                  if (controlled) search.onChange(e.target.value);
                  else setQuery(e.target.value);
                  setPage(0);
                }}
                // Row-only sizing. In the phone column a `flexBasis` sizes the
                // *height*, and the 220px basis stretched the field into a tall
                // empty box above the row count.
                sx={{ flexGrow: { md: 1 }, flexBasis: { md: 220 }, minWidth: { md: 200 } }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icons.Search size={iconSize.md} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
            {/* The count and the actions share one row on a phone, the count
                left and the buttons right, instead of a line each. From `md`
                the wrapper dissolves (`display: contents`) and they rejoin the
                toolbar row as before. */}
            <Box
              data-testid="table-toolbar-meta"
              sx={{
                display: { xs: 'flex', md: 'contents' },
                alignItems: 'center',
                flexWrap: 'wrap',
                // A second line (the actions, when they do not fit beside the
                // count) lines up on the right, under the first line's buttons.
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  mr: { xs: 'auto', md: 0 },
                }}
              >
                {pagination?.total !== undefined
                  ? t('common.table.count', { count: pagination.total })
                  : sorted.length === rows.length
                    ? t('common.table.count', { count: rows.length })
                    : t('common.table.countFiltered', { count: sorted.length, total: rows.length })}
              </Typography>
              {pickable.some((c) => c.optional) && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Icons.Columns3 size={iconSize.md} />}
                    onClick={(e) => setColumnsAnchor(e.currentTarget)}
                    sx={{ flexShrink: 0 }}
                  >
                    {t('common.table.columns')}
                  </Button>
                  <Menu
                    anchorEl={columnsAnchor}
                    open={Boolean(columnsAnchor)}
                    onClose={() => setColumnsAnchor(null)}
                  >
                    {pickable.map((column) => (
                      <MenuItem
                        key={column.key}
                        onClick={() =>
                          setHidden((prev) => {
                            const next = new Set(prev);
                            // The last visible column cannot be hidden — an empty
                            // table has nothing to read and no way back.
                            if (next.has(column.key)) next.delete(column.key);
                            else if (shown.length > 1) next.add(column.key);
                            return next;
                          })
                        }
                      >
                        <Checkbox size="small" checked={!hidden.has(column.key)} sx={{ mr: 1 }} />
                        {column.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
              {selection && selection.selected.size > 0 && selection.actions && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {t('common.table.selected', { count: selection.selected.size })}
                  </Typography>
                  {selection.actions}
                </Stack>
              )}
              {/* A flex row with a gap, so a page passing two controls — export
                and new, say — gets them spaced rather than butted together. */}
              {action && (
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {action}
                </Box>
              )}
            </Box>
          </Stack>
        </HairlineCard>
      )}

      <HairlineCard sx={{ overflow: 'hidden' }}>
        {isLoading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : stacked ? (
          visible.length === 0 ? (
            <Typography sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
              {rows.length === 0
                ? (emptyLabel ?? t('common.table.empty'))
                : t('common.table.noMatches')}
            </Typography>
          ) : (
            <Box role="region" aria-label={label}>
              <StackedRows
                rows={visible}
                getRowKey={getRowKey}
                onRowClick={onRowClick}
                rowLabel={rowLabel}
                columns={shown.map((column) => ({
                  key: column.key,
                  label: column.label,
                  primary: column.key === linkColumnKey,
                  actions: column.actions ?? column.key === 'actions',
                  cell:
                    rowHref && column.key === linkColumnKey
                      ? (row: Row) => (
                          <Link
                            component={linkComponent ?? 'a'}
                            {...(linkComponent ? { to: rowHref(row) } : { href: rowHref(row) })}
                            underline="hover"
                            color="inherit"
                          >
                            {column.cell(row)}
                          </Link>
                        )
                      : column.cell,
                }))}
              />
            </Box>
          )
        ) : (
          <RowActionsCompactContext.Provider value={compact}>
            <Box
              ref={scrollRef}
              data-table-scroll=""
              data-overflowing={overflowing ? '' : undefined}
              // A scroll container that only answers the mouse is a 2.1.1
              // failure, so a table that is expected to scroll is a focusable
              // labelled region reachable by keyboard.
              {...(minWidth ? { tabIndex: 0, role: 'region', 'aria-label': label } : null)}
              sx={TABLE_SCROLL_SX}
            >
              {body}
            </Box>
          </RowActionsCompactContext.Provider>
        )}

        {!isLoading && (pagination ? pageCount > 1 : pageSize && sorted.length > pageSize) && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {t('common.table.pageOf', { page: safePage + 1, pages: pageCount })}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                aria-label={t('common.pagination.previous')}
                disabled={safePage === 0}
                onClick={() => goToPage(Math.max(0, safePage - 1))}
              >
                <Icons.ChevronLeft size={iconSize.md} />
              </IconButton>
              <IconButton
                size="small"
                aria-label={t('common.pagination.next')}
                disabled={safePage >= pageCount - 1}
                onClick={() => goToPage(Math.min(pageCount - 1, safePage + 1))}
              >
                <Icons.ChevronRight size={iconSize.md} />
              </IconButton>
            </Stack>
          </Stack>
        )}
      </HairlineCard>
    </Stack>
  );
}
